import { useState, useEffect, useRef } from 'react'
import { estimatesService, type PresenceUser } from '@/services/estimates-service'

/**
 * Who else has this estimate open.
 *
 * Two intervals, not one. The heartbeat WRITES a row saying "still here" and
 * stays at 30s, comfortably inside the 60s the server treats as stale. The poll
 * only READS, and runs twice as often: whoever arrives second sees the other
 * person immediately, but the one already editing used to wait up to a full
 * heartbeat to find out they had company — and they are the one with unsaved
 * thinking in their head. Reads are cheap; writes are the ones worth spacing.
 */
const HEARTBEAT_MS = 30_000
const POLL_MS = 15_000

export function useEstimatePresence(estimateId: string | undefined) {
  const [others, setOthers] = useState<PresenceUser[]>([])
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (!estimateId) return

    async function ping() {
      try { await estimatesService.pingPresence(estimateId!) } catch { /* silent */ }
    }

    async function poll() {
      try {
        const users = await estimatesService.getPresence(estimateId!)
        setOthers(users)
      } catch { /* silent */ }
    }

    ping()
    poll()
    heartbeatRef.current = setInterval(ping, HEARTBEAT_MS)
    pollRef.current = setInterval(poll, POLL_MS)

    return () => {
      if (heartbeatRef.current) clearInterval(heartbeatRef.current)
      if (pollRef.current) clearInterval(pollRef.current)
      setOthers([])
      estimatesService.leavePresence(estimateId!).catch(() => {})
    }
  }, [estimateId])

  return others
}
